import { LCBatchesPresentationOrderEnum } from '@kaltura-ott/tvpil';
import { useMemo } from 'react';

import { composeChannels } from './composeChannels';
import { useChannelAssets } from './useChannelAssets';
import { useChannelCollection } from './useChannelCollection';
import { useEntitledChannels } from './useEntitledChannels';
import { useFavoriteChannelWithInitial } from './useFavoriteChannelWithInitial';

type UseMyFavoriteChannelsProps = {
  referenceId: string;
};

export function useMyFavoriteChannels({ referenceId }: UseMyFavoriteChannelsProps) {
  const { channelAssetsMap } = useChannelAssets();
  const { entitledChannels } = useEntitledChannels();
  const { channelCollection, error, isLoading } = useChannelCollection({ referenceId });

  const { favoriteChannels, initialFavoriteChannels, refetch, reorder, isRefetching } = useFavoriteChannelWithInitial();

  const channels = useMemo(() => {
    if (!channelAssetsMap || !entitledChannels || !channelCollection || !favoriteChannels) {
      return;
    }

    if (!initialFavoriteChannels) {
      return;
    }

    const sortedChannels = composeChannels({
      channelAssetsMap,
      channelCollection,
      entitledChannels,
      favoriteChannels: initialFavoriteChannels,
      orderBatches: [LCBatchesPresentationOrderEnum.fav, LCBatchesPresentationOrderEnum.lcn],
    });

    const channelsWithUpdatedFavorites = sortedChannels.map((channel) => ({
      ...channel,
      isFavorite: favoriteChannels.has(channel.id),
    }));

    return channelsWithUpdatedFavorites;
  }, [channelAssetsMap, channelCollection, entitledChannels, favoriteChannels, initialFavoriteChannels]);

  return {
    channels,
    error,
    refetch,
    reorder,
    isLoading: isLoading || isRefetching || !initialFavoriteChannels,
  };
}
